import { createContext, useContext, useState, useCallback, type ReactNode } from "react";

export interface Currency {
  code: string;
  symbol: string;
  name: string;
  locale: string;
}

export const CURRENCIES: Currency[] = [
  { code: "USD", symbol: "$",    name: "US Dollar",          locale: "en-US" },
  { code: "EUR", symbol: "€",    name: "Euro",               locale: "de-DE" },
  { code: "GBP", symbol: "£",    name: "British Pound",      locale: "en-GB" },
  { code: "INR", symbol: "₹",    name: "Indian Rupee",       locale: "en-IN" },
  { code: "JPY", symbol: "¥",    name: "Japanese Yen",       locale: "ja-JP" },
  { code: "AED", symbol: "د.إ",  name: "UAE Dirham",         locale: "ar-AE" },
  { code: "CAD", symbol: "C$",   name: "Canadian Dollar",    locale: "en-CA" },
  { code: "AUD", symbol: "A$",   name: "Australian Dollar",  locale: "en-AU" },
  { code: "SGD", symbol: "S$",   name: "Singapore Dollar",   locale: "en-SG" },
  { code: "CHF", symbol: "CHF",  name: "Swiss Franc",        locale: "de-CH" },
];

const STORAGE_KEY = "payflow_currency";

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (code: string) => void;
  fmt: (amount: number) => string;
  fmtShort: (amount: number) => string;
}

const CurrencyContext = createContext<CurrencyContextType | null>(null);

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrencyState] = useState<Currency>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return CURRENCIES.find((c) => c.code === stored) ?? CURRENCIES[0];
  });

  const setCurrency = useCallback((code: string) => {
    const found = CURRENCIES.find((c) => c.code === code);
    if (!found) return;
    setCurrencyState(found);
    localStorage.setItem(STORAGE_KEY, code);
  }, []);

  const fmt = useCallback(
    (amount: number): string => {
      if (currency.code === "JPY") {
        return `${currency.symbol}${Math.round(amount).toLocaleString(currency.locale)}`;
      }
      return `${currency.symbol}${amount.toFixed(2)}`;
    },
    [currency],
  );

  const fmtShort = useCallback(
    (amount: number): string => {
      const sym = currency.symbol;
      if (amount >= 1_000_000) return `${sym}${(amount / 1_000_000).toFixed(1)}M`;
      if (amount >= 1_000) return `${sym}${(amount / 1_000).toFixed(1)}k`;
      return fmt(amount);
    },
    [currency, fmt],
  );

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, fmt, fmtShort }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used within CurrencyProvider");
  return ctx;
}
